/* eslint-disable max-lines */
import { Input, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, DialogContent, Stack, ThemeProvider } from "@mui/material";
import { type ApiResponse } from "@src/appV2/api";
import { type OtpRequestChannel } from "@src/appV2/Auth/api/useRequestOtp";
import { type ValidateOtpResponse } from "@src/appV2/Auth/api/useValidateOtp";
import { AuthMode } from "@src/appV2/Auth/LoginSignUp/libs";
import FetchOtpButtonInDevelopmentMode from "@src/appV2/Debug/components/FetchOtpButtonInDevelopmentMode";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import {
  APP_V2_USER_EVENTS,
  extractPhoneNumberDigits,
  ProgressDialog,
  useToast,
} from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { SupportArticles, SupportContext } from "@src/appV2/support/constants";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { type IdentifyProvider, IdentifyProviderEnum } from "../../api/types";
import { ValidateOtpError } from "../../api/useValidateOtp";
import { ResendCodeSection } from "./components/ResendCodeSection";

const DEFAULT_OTP_LENGTH = 6;
const COGNITO_LOGIN_OTP_LENGTH = 8;

function getOtpLength(identityProvider: IdentifyProvider, isSignup: boolean): number {
  // Cognito signIn (requestPhoneOtp) uses 8-digit OTP, everything else uses 6-digit
  if (identityProvider === IdentifyProviderEnum.cognito && !isSignup) {
    return COGNITO_LOGIN_OTP_LENGTH;
  }
  return DEFAULT_OTP_LENGTH;
}

function getOtpPlaceholder(otpLength: number): string {
  return "0".repeat(otpLength);
}

function createVerifyOtpFormSchema(otpLength: number) {
  const invalidOtpMessage = `Please enter a valid ${otpLength} digit code`;

  return z.object({
    otp: z.string().length(otpLength, { message: invalidOtpMessage }),
  });
}

interface ValidateOtpInput {
  phone: string;
  otp: string;
  isSignup: boolean;
}

interface Props {
  readonly modalState: UseModalState;
  readonly phoneNumber: string;
  readonly onSuccess: (params: {
    firebaseToken: string;
    agentDeletionRequestCancelled: boolean;
  }) => Promise<void>;
  readonly authMode: AuthMode;
  readonly isAuthenticating?: boolean;
  readonly identityProvider: IdentifyProvider;
  readonly cognitoIsSignup?: boolean;
  readonly resendOtp: (params: {
    phone: string;
    isSignup: boolean;
    channel?: OtpRequestChannel;
  }) => Promise<unknown>;
  readonly validateOtp: (input: ValidateOtpInput) => Promise<ApiResponse<ValidateOtpResponse>>;
  readonly isValidatingOtp: boolean;
}

function getErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object" || !("message" in error)) {
    return undefined;
  }

  const { message } = error as { message: unknown };
  return typeof message === "string" ? message : undefined;
}

export function VerifyOtpDialog(props: Props) {
  const {
    modalState,
    phoneNumber,
    onSuccess,
    authMode,
    isAuthenticating = false,
    identityProvider,
    cognitoIsSignup,
    resendOtp,
    validateOtp,
    isValidatingOtp,
  } = props;

  const isSignup = cognitoIsSignup ?? authMode === AuthMode.SIGN_UP;

  const { showErrorToast } = useToast();
  const { howToContactSupportLink } = useSupportLinks();
  const theme = getTheme();

  const otpLength = getOtpLength(identityProvider, isSignup);

  const formMethods = useForm({
    defaultValues: {
      otp: "",
    },
    resolver: zodResolver(createVerifyOtpFormSchema(otpLength)),
  });

  const {
    formState: { errors },
    reset,
    register,
    handleSubmit,
    setError,
    setValue,
  } = formMethods;

  const formRef = useRef<HTMLFormElement>();
  const otpFieldProps = register("otp");

  const isTwilioVerificationApiEnabled = useCbhFlag(CbhFeatureFlag.TWILIO_VERIFICATION_API, {
    defaultValue: false,
  });

  const { countdownIsLoading, startTimer, secondsLeft } = useCountdownTimer({
    durationInSeconds: 25,
  });

  useEffect(() => {
    if (modalState.modalIsOpen) {
      reset();
    }
  }, [modalState.modalIsOpen, reset]);

  const handleResendCode = useCallback(
    async (channel: OtpRequestChannel = "sms") => {
      if (countdownIsLoading) {
        return;
      }

      try {
        await resendOtp({
          phone: phoneNumber,
          isSignup: authMode === AuthMode.SIGN_UP,
          channel,
        });

        logEvent(APP_V2_USER_EVENTS.RESEND_OTP, {
          phoneNumber,
          isSignup: authMode === AuthMode.SIGN_UP,
          channel,
        });
      } catch (error) {
        const message = getErrorMessage(error);
        showErrorToast(
          message ?? "Something went wrong while requesting an OTP code. Please try again."
        );
      } finally {
        startTimer();
      }
    },
    [countdownIsLoading, phoneNumber, authMode, resendOtp, showErrorToast, startTimer]
  );

  return (
    <>
      <ThemeProvider theme={theme}>
        <FullScreenDialog modalState={modalState} variant="tertiary">
          <PageHeaderTopRow
            onClickBack={() => {
              modalState.closeModal();
              logEvent(APP_V2_USER_EVENTS.CLOSE_VERIFY_OTP_DIALOG);
            }}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              padding: theme.spacing(6),
              paddingBottom: 0,
            }}
          >
            <Box
              ref={formRef}
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
              onSubmit={handleSubmit(async (formData) => {
                logEvent(APP_V2_USER_EVENTS.SUBMIT_AUTH_OTP_FORM);

                const { data } = await validateOtp({
                  phone: extractPhoneNumberDigits(phoneNumber),
                  otp: formData.otp,
                  isSignup,
                });

                if (data.message === "otpIsValid") {
                  await onSuccess({
                    firebaseToken: data.data.firebaseToken,
                    agentDeletionRequestCancelled: data.data.agentDeletionRequestCancelled,
                  });
                  return true;
                }

                if (
                  data.error === ValidateOtpError.INVALID_OTP ||
                  data.error === ValidateOtpError.OTP_EXPIRED ||
                  data.error === ValidateOtpError.INVALID_USER_FOR_LOGIN ||
                  data.error === ValidateOtpError.INVALID_USER_FOR_SIGNUP
                ) {
                  const formError = { type: "server", message: data.message };
                  setError("otp", formError);
                  logEvent(APP_V2_USER_EVENTS.SUBMIT_AUTH_OTP_FORM_ERROR, {
                    identityProvider,
                  });
                  return false;
                }

                return false;
              })}
            >
              <DialogContent>
                <Stack sx={{ textAlign: "center", alignItems: "center" }} gap={8} marginBottom={8}>
                  <Title variant="h3" component="h3" sx={{ textWrap: "balance" }}>
                    We just texted you. What is the code?
                  </Title>

                  <Stack gap={2}>
                    <Text variant="body1">Sent to: {phoneNumber}</Text>
                  </Stack>
                </Stack>

                <Box sx={{ marginBottom: 6, textAlign: "center" }}>
                  <Input
                    placeholder={getOtpPlaceholder(otpLength)}
                    autoComplete="one-time-code"
                    type="number"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: otpLength,
                      "aria-label": "Verification Code",
                      style: { textAlign: "center" },
                    }}
                    sx={{
                      fontSize: theme.typography.h4.fontSize,
                      fontWeight: theme.typography.h4.fontWeight,
                      letterSpacing: theme.spacing(1),
                      maxWidth: "12rem",
                    }}
                    {...otpFieldProps}
                    error={Boolean(errors.otp)}
                    onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      await otpFieldProps.onChange(event);
                      // Auto-submit when OTP is complete
                      // Feature detection: requestSubmit() is not supported on Safari iOS < 16
                      // Fallback: Users can manually click Continue button (now inside form)
                      if (
                        value.length === otpLength &&
                        typeof formRef.current?.requestSubmit === "function"
                      ) {
                        formRef.current?.requestSubmit();
                      }
                    }}
                  />
                  {errors.otp?.message && (
                    <Text
                      variant="caption"
                      sx={{ marginTop: 2, color: theme.palette.intent?.error.text }}
                    >
                      {errors.otp.message}
                    </Text>
                  )}
                </Box>

                <ResendCodeSection
                  showContactSupport
                  isTwilioEnabled={isTwilioVerificationApiEnabled}
                  countdownIsLoading={countdownIsLoading}
                  secondsLeft={secondsLeft}
                  onContactSupport={async () => {
                    await openInBrowser({ url: howToContactSupportLink });
                    logEvent(APP_V2_USER_EVENTS.OPENED_SUPPORT_ARTICLE, {
                      articleLink: howToContactSupportLink,
                      articleName: SupportArticles.HOW_TO_CONTACT_SUPPORT,
                      supportContext:
                        authMode === AuthMode.LOGIN
                          ? SupportContext.LOGIN_OTP_DELIVERY
                          : SupportContext.SIGN_UP_OTP_DELIVERY,
                    });
                  }}
                  onClick={handleResendCode}
                />

                <FetchOtpButtonInDevelopmentMode
                  phoneNumber={phoneNumber}
                  onSuccess={(otp) => {
                    setValue("otp", otp);
                    // Feature detection: requestSubmit() is not supported on Safari iOS < 16
                    // Fallback: Users can manually click Continue button (now inside form)
                    if (typeof formRef.current?.requestSubmit === "function") {
                      formRef.current?.requestSubmit();
                    }
                  }}
                />
              </DialogContent>

              <DialogFooter orientation="vertical">
                <LoadingButton
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  isLoading={isValidatingOtp}
                  aria-label="Continue"
                >
                  Continue
                </LoadingButton>
              </DialogFooter>
            </Box>
          </Box>
        </FullScreenDialog>
      </ThemeProvider>
      <ProgressDialog
        modalIsOpen={isValidatingOtp || isAuthenticating}
        messageContent="Verifying..."
      />
    </>
  );
}
/* eslint-enable max-lines */
